import { useMemo, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { GALLERY, GALLERY_CATEGORIES, GalleryItem, imgSrc } from "../lib/gallery";
import { useEnquiry } from "../context/EnquiryContext";
import { scrollToId } from "../lib/scroll";
import { Reveal } from "./Reveal";
import { Lightbox } from "./Lightbox";

export function Gallery() {
  const [cat, setCat] = useState<(typeof GALLERY_CATEGORIES)[number]>("All");
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  const { setReference } = useEnquiry();

  const items = useMemo(
    () => (cat === "All" ? GALLERY : GALLERY.filter((g) => g.category === cat)),
    [cat]
  );

  const customise = (item: GalleryItem) => {
    setReference({ id: item.id, title: item.title, category: item.category });
    setOpenIdx(null);
    toast.success(`“${item.title}” added to your enquiry — tell us your idea below.`);
    setTimeout(() => scrollToId("customise"), 180);
  };

  return (
    <section id="gallery" className="py-20 sm:py-24" data-testid="gallery">
      <div className="max-w-6xl mx-auto px-5 sm:px-6">
        <Reveal>
          <div className="max-w-2xl">
            <p className="flex items-center gap-2 text-xs sm:text-sm uppercase tracking-[0.22em] text-burgundy font-medium">
              <span className="inline-block h-2 w-2 rounded-full bg-brass" aria-hidden="true" />
              Our rugs
            </p>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-forest">
              A rug with your story woven in.
            </h2>
            <p className="mt-4 text-base leading-relaxed text-charcoal/75">
              Small details. Big personality. Every piece below starts as an idea like yours —
              pick one to customise, or use them as a jumping-off point. And a rug is never just
              for the floor: we tuft car rugs, ceiling rugs, desk rugs and wall rugs too.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-8 flex flex-wrap gap-2" role="tablist" aria-label="Filter designs by category">
            {GALLERY_CATEGORIES.map((c) => {
              const count = c === "All" ? GALLERY.length : GALLERY.filter((g) => g.category === c).length;
              const active = cat === c;
              return (
                <button
                  key={c}
                  role="tab"
                  aria-selected={active}
                  onClick={() => { setCat(c); setOpenIdx(null); }}
                  data-testid={`gallery-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={`rounded-full px-4 py-2 text-sm font-medium border transition-all duration-200 ${
                    active
                      ? "bg-forest text-ivory border-forest shadow-tuft"
                      : "bg-ivory-soft text-charcoal/75 border-forest/15 hover:border-forest/40 hover:-translate-y-0.5"
                  }`}
                >
                  {c}
                  <span className={`ml-1.5 text-xs ${active ? "text-ivory/70" : "text-charcoal/40"}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </Reveal>

        <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
          {items.map((item, i) => (
            <figure
              key={item.id}
              className="group relative rounded-2xl overflow-hidden bg-brass-soft border border-forest/10 shadow-tuft transition-shadow duration-300 hover:shadow-tuft-lg"
              data-testid={`rug-card-${item.id}`}
            >
              <button
                className="block w-full text-left"
                onClick={() => setOpenIdx(i)}
                aria-label={`Open ${item.title} — ${item.direction}`}
                data-testid={`rug-open-${item.id}`}
              >
                <span className="block aspect-square overflow-hidden">
                  <img
                    src={imgSrc(item.img, 700)}
                    alt={item.alt}
                    width={700}
                    height={700}
                    loading={i < 4 ? "eager" : "lazy"}
                    decoding="async"
                    className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.06]"
                  />
                </span>
              </button>
              <span className="absolute top-3 left-3 rounded-full bg-ivory/90 border border-forest/10 text-forest text-[10px] sm:text-[11px] font-medium px-2.5 py-1">
                {item.badge === "concept" ? "Concept render" : "Reference photo"}
              </span>
              <figcaption className="absolute inset-x-0 bottom-0 p-3 sm:p-4 pt-10 bg-gradient-to-t from-forest/90 via-forest/45 to-transparent text-ivory">
                <p className="font-serif text-base sm:text-lg leading-tight">{item.title}</p>
                <p className="text-[11px] sm:text-xs text-ivory/75 mt-0.5">{item.direction}</p>
                <button
                  onClick={() => customise(item)}
                  data-testid={`rug-customise-${item.id}`}
                  className="mt-2.5 inline-flex items-center rounded-full bg-ivory text-forest text-xs font-medium px-3.5 py-1.5 transition-all duration-200 hover:-translate-y-0.5 hover:bg-brass hover:text-charcoal"
                >
                  Customise This Design
                </button>
              </figcaption>
            </figure>
          ))}
        </div>

        <Reveal delay={0.05}>
          <p className="mt-8 text-xs leading-relaxed text-charcoal/55 max-w-3xl">
            Renders marked “Concept render” are original visualisations made in-house — none of the
            rugs on this page are presented as completed customer work. Fan-inspired artwork is not
            affiliated with, endorsed by, or licensed by any rights holder.
          </p>
        </Reveal>
      </div>

      <AnimatePresence>
        {openIdx !== null && items[openIdx] && (
          <Lightbox
            items={items}
            index={openIdx}
            onClose={() => setOpenIdx(null)}
            onNavigate={(dir) =>
              setOpenIdx((prev) => {
                const n = items.length;
                return prev === null ? prev : (prev + dir + n) % n;
              })
            }
            onCustomise={customise}
          />
        )}
      </AnimatePresence>
    </section>
  );
}
